import type { ColumnsType } from "antd/es/table";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatDateNoTimezone } from "../../../../utils/dateService";
import type { TransactionStatus } from "../../../../types/transactions";
import {
  formatTransactionBalance,
  formatTransactionMoney,
  renderCardBankInfoCell,
  renderOptionalValue,
  renderPaymentTypeLabel,
  renderReasonCell,
  renderTransactionStatus,
} from "../shared/transactionColumnHelpers";
import type { PaymentRow } from "./mockPayments";
import type { PaymentSortBy, PaymentSortOrder } from "./applyPaymentQuery";

const toAntdSortOrder = (
  sortBy: PaymentSortBy | null,
  sortOrder: PaymentSortOrder | null,
  column: PaymentSortBy,
): "ascend" | "descend" | null => {
  if (sortBy !== column || !sortOrder) {
    return null;
  }
  return sortOrder === "asc" ? "ascend" : "descend";
};

type PaymentsTableColumnsArgs = {
  currencySymbol?: string;
  sortBy: PaymentSortBy | null;
  sortOrder: PaymentSortOrder | null;
};

export const getPaymentsTableColumns = ({
  currencySymbol,
  sortBy,
  sortOrder,
}: PaymentsTableColumnsArgs): ColumnsType<PaymentRow> => [
  {
    title: AppStrings.DATE_TIME,
    dataIndex: "timestamp",
    key: "timestamp",
    width: 150,
    render: (text: string) => (
      <p className="timestamp-text">
        {formatDateNoTimezone(text, "DD MMMM YYYY, HH:mm:ss")}
      </p>
    ),
  },
  {
    title: AppStrings.ID,
    dataIndex: "transaction_id",
    key: "transaction_id",
    width: 250,
  },
  {
    title: AppStrings.MONEY_TYPE,
    dataIndex: "wallet_type",
    key: "wallet_type",
    width: 150,
  },
  {
    title: AppStrings.AMOUNT,
    dataIndex: "amount",
    key: "amount",
    width: 100,
    sorter: true,
    sortOrder: toAntdSortOrder(sortBy, sortOrder, "amount"),
    render: (text: string | number | null, record: PaymentRow) =>
      formatTransactionMoney(text, record.wallet_type, currencySymbol),
  },
  {
    title: AppStrings.TYPE,
    dataIndex: "transaction_type",
    key: "transaction_type",
    width: 100,
  },
  {
    title: AppStrings.TRANSACTION_METHOD,
    dataIndex: "transaction_method",
    key: "transaction_method",
    width: 200,
    render: (_text: unknown, record: PaymentRow) => (
      <p
        className={`transaction-method-text ${
          record?.by_admin ? "by-admin" : "not-by-admin"
        }`}
      >
        {record?.title ?? undefined}
      </p>
    ),
  },
  {
    title: AppStrings.PAYMENT_GATEWAY,
    dataIndex: "payment_gateway",
    key: "payment_gateway",
    width: 180,
    render: (text: string | null | undefined) => renderOptionalValue(text),
  },
  {
    title: AppStrings.PAYMENT_TYPE,
    dataIndex: "payment_type",
    key: "payment_type",
    width: 180,
    render: (_text: unknown, record: PaymentRow) =>
      renderPaymentTypeLabel(record),
  },
  {
    title: AppStrings.CARD_BANK_INFO,
    dataIndex: "card_bank_info",
    key: "card_bank_info",
    width: 180,
    render: (text: string | null | undefined, record: PaymentRow) =>
      renderCardBankInfoCell(text, record),
  },
  {
    title: AppStrings.REASON,
    dataIndex: "reason",
    key: "reason",
    width: 150,
    render: (text: string | null | undefined, record: PaymentRow) =>
      renderReasonCell(text, record),
  },
  {
    title: AppStrings.BALANCE,
    dataIndex: "total_balance",
    key: "total_balance",
    width: 150,
    sorter: true,
    sortOrder: toAntdSortOrder(sortBy, sortOrder, "total_balance"),
    render: (text: string | number | null, record: PaymentRow) =>
      formatTransactionBalance(text, record, currencySymbol),
  },
  {
    title: AppStrings.STATUS,
    dataIndex: "status",
    key: "status",
    width: 150,
    render: (text: TransactionStatus) => renderTransactionStatus(text),
  },
];
